import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { OrganizationDataService } from '../../../core/api-services/organization-data.service';
import { OrganizationCustomer } from '../../../core/response-models/organization-customer.model';
import { OrganizationCustomerField } from '../../../core/response-models/organization-customer-field.model';

/**
 * Organization Customers Tab Component
 *
 * Displays a read-only paginated and searchable table of customers for an organization.
 * Supports expandable rows that lazy-load field data on first expand.
 * Fields are cached in a Map to avoid re-fetching on subsequent expands.
 */
@Component({
  selector: 'app-organization-customers-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, PhoneFormatPipe],
  templateUrl: './organization-customers-tab.component.html',
  styleUrls: ['./organization-customers-tab.component.scss']
})
export class OrganizationCustomersTabComponent implements OnInit {
  @Input() organizationId!: string;

  customers: OrganizationCustomer[] = [];
  loading = true;
  errorMessage = '';

  // Expand state
  expandedCustomers: Set<string> = new Set();
  customerFields: Map<string, OrganizationCustomerField[]> = new Map();
  loadingFields: Set<string> = new Set();

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalCustomers = 0;
  totalPages = 0;

  // Search
  searchQuery = '';

  constructor(private organizationData: OrganizationDataService) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.errorMessage = 'Organization ID is required';
      this.loading = false;
      return;
    }

    this.loadCustomers();
  }

  async loadCustomers(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const page = await firstValueFrom(
        this.organizationData.getCustomers(this.organizationId, this.currentPage, this.pageSize, this.searchQuery)
      );
      this.customers = page.items;
      this.totalCustomers = page.paging.totalItems;
      this.totalPages = page.paging.totalPages;
    } catch (error: any) {
      console.error('Failed to load customers:', error);
      this.errorMessage = 'Failed to load customers. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadCustomers();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadCustomers();
    }
  }

  toggleCustomer(customerId: string): void {
    if (this.expandedCustomers.has(customerId)) {
      this.expandedCustomers.delete(customerId);
    } else {
      this.expandedCustomers.add(customerId);
      // Lazy-load fields on first expand only
      if (!this.customerFields.has(customerId)) {
        this.loadFields(customerId);
      }
    }
  }

  isExpanded(customerId: string): boolean {
    return this.expandedCustomers.has(customerId);
  }

  isLoadingFields(customerId: string): boolean {
    return this.loadingFields.has(customerId);
  }

  getFields(customerId: string): OrganizationCustomerField[] {
    return this.customerFields.get(customerId) || [];
  }

  async loadFields(customerId: string): Promise<void> {
    this.loadingFields.add(customerId);

    try {
      const fields = await firstValueFrom(this.organizationData.getCustomerFields(this.organizationId, customerId));
      this.customerFields.set(customerId, fields);
    } catch (error: any) {
      console.error('Failed to load fields for customer:', error);
    } finally {
      this.loadingFields.delete(customerId);
    }
  }

  formatLocation(city: string | null, state: string | null): string {
    const parts = [city, state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCustomers);
  }
}
